import { API } from '../api/api.constant';

/**
 * WS-7199. Rebuilds the tab's session record before the application is drawn.
 *
 * Authentication lives in the session_id COOKIE, which every tab on the domain can see, while the
 * record of who the user is and which workshop they belong to lives in sessionStorage, which
 * browsers deliberately keep private to a single tab. Nothing reconciled the two. A brand new tab
 * therefore passed the logged in check on the cookie and then drew itself against an empty record,
 * which is the blank screen. Chrome's Duplicate Tab copies sessionStorage into the copy, which is
 * why duplicating a tab worked and a link from an email did not.
 *
 * This has to run before platformBrowserDynamic().bootstrapModule, and that is the only point early
 * enough. The AngularJS config block in core.translate-config.ajs.ts reads session_object straight
 * out of sessionStorage, and the header and the sidebar sit outside ui-view, so by the time an
 * Angular service, an APP_INITIALIZER or a router hook could run, the empty record has already been
 * read and drawn against.
 *
 * It deliberately does not go through SessionService. That service, and JsonService, CacheService
 * and DomainParserService underneath it, resolve UIRouter or UrlService. In a hybrid application
 * both come from the AngularJS $injector by way of UIRouterUpgradeModule, and @angular/upgrade only
 * populates $injector inside upgrade.bootstrap(). Asking Angular DI for any of them beforehand
 * throws "Trying to get the AngularJS injector before it being set", which would break every page
 * load rather than just the new tab. A plain fetch has no such ordering problem.
 */

export const SESSION_STORAGE_KEY = 'session_object';

const GROUP_RIGHTS_STORAGE_KEY = 'group_rights';

// A new tab has the cookie and no record. Someone who is not logged in has neither, and is left
// alone so that the existing redirect to the login page keeps working exactly as it does today.
function hasSessionCookie () : boolean {
  const match = document.cookie.match(/(?:^|;)\s*session_id=([^;]*)/);
  const value = match ? match[1] : '';

  // The cookie holds a JSON encoded id, so a logged out tab can hold the two character string "".
  return !!value && value !== '%22%22' && value !== '""' && value !== 'null';
}

function hasSessionRecord () : boolean {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);

    return !!stored && Object.keys(JSON.parse(stored) || {}).length > 0;
  }
  catch {
    return false;
  }
}

function getJson ( path : string ) : Promise<any> {
  return fetch(`${ API.url }${ path }`, {
    credentials: 'include',
    headers    : { Accept: 'application/json' }
  })
  .then(( response : Response ) => {
    // A login that has expired or been revoked answers 401 here. That is what sends the tab to the
    // login page instead of letting it draw a blank or a half loaded screen.
    if (!response.ok) {
      throw new Error(`${ path } answered ${ response.status }`);
    }

    return response.json();
  });
}

export function ensureSessionRecord () : Promise<void> {
  if (!hasSessionCookie() || hasSessionRecord()) {
    return Promise.resolve();
  }

  return getJson('/system/refresh_session')
  .then(( session : any ) => {
    // refresh_session reloads the Session the cookie already names rather than issuing a new one,
    // so the cookie itself does not need rewriting here. A body carrying no session_id is an error
    // payload answered with 200, and counts as a failed rebuild rather than a usable record.
    if (!session || !session.session_id) {
      throw new Error('refresh_session returned no session');
    }

    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));

    return getJson('/system/user_group_rights');
  })
  .then(( rights : any ) => {
    // A group holding no rights at all is an empty array, which is a usable answer. A body with no
    // group_rights_attributes at all is not, and is treated the same as a failed rebuild.
    if (!rights || !rights.group_rights_attributes) {
      throw new Error('user_group_rights returned no rights');
    }

    const groupRights = rights.group_rights_attributes
      .map(( right : any ) => right.object_name);

    sessionStorage.setItem(GROUP_RIGHTS_STORAGE_KEY, JSON.stringify(groupRights));
  })
  // Both endpoints sit behind validate_and_update_session, so a login that has expired or been
  // revoked fails here rather than rendering. A user whose group has been removed is answered 204
  // by user_group_rights and lands here too, which is correct: a tab with a session but no
  // permissions is the half loaded page this ticket exists to stop showing.
  .catch(() => {
    window.location.href = '/login.html';

    // The browser is already navigating away. Never settling keeps the application from booting up
    // behind the redirect, which is what would put the blank screen on show one last time.
    return new Promise<void>(() => undefined);
  });
}
